// src/hooks/useAdminConversations.js

import { useState, useEffect } from 'react';
import adminApiService from '../services/adminApiService';

export function useAdminConversations() {
  const [conversations, setConversations] = useState([]);
  const [allConversations, setAllConversations] = useState([]); // Store original data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState('');

  // Load conversations on mount
  useEffect(() => {
    loadConversations();
  }, []);

  // Auto-filter when searchTerm or selectedDate changes
  useEffect(() => {
    const timer = setTimeout(() => {
      applyFilters();
    }, 300); // 300ms debounce for search term

    return () => clearTimeout(timer);
  }, [searchTerm, selectedDate, allConversations]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await adminApiService.getAllConversations();
      setAllConversations(data);
      setConversations(data);
    } catch (err) {
      setError(err.message);
      console.error('Error loading conversations:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let results = [...allConversations];
    
    // Apply search term filter (chat name or session ID)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      results = results.filter(conv => 
        (conv.chatName?.toLowerCase().includes(term)) ||
        (conv.id?.toLowerCase().includes(term))
      );
    }
    
    // Apply date filter (exact date match)
    if (selectedDate) {
      results = results.filter(conv => {
        if (!conv.startTime) return false;
        
        const convDate = new Date(conv.startTime);
        const filterDate = new Date(selectedDate);
        
        // Compare only year, month, and day (ignore time)
        return (
          convDate.getFullYear() === filterDate.getFullYear() &&
          convDate.getMonth() === filterDate.getMonth() &&
          convDate.getDate() === filterDate.getDate()
        );
      });
    }
    
    setConversations(results);
  };

  const handleSearch = () => {
    // Manual search trigger (optional, filters already auto-apply)
    applyFilters();
  };

  const refreshConversations = () => {
    // Clear filters and reload
    setSearchTerm('');
    setSelectedDate('');
    loadConversations();
  };

  return {
    conversations,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    selectedDate,
    setSelectedDate,
    handleSearch,
    refreshConversations
  };
}
